
// Library imports.
import axios, { AxiosResponse } from "axios";
import { baseWorker } from "../baseWorker";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';



// App imports.
import { config } from "../config";
import axiosInstance from '../axiosInstance';
//import { green100 } from "material-ui/styles/colors";


export interface IDataPoint {
  y: number;
  prefix?: string;
  suffix?: string;
  displayVal?: string;
  // Additional optional properties that ChartParent uses when building series:
  name?: string;       // e.g. for labeling individual points
  type?: string;       // e.g. "column" or "spline" if needed on a per-point basis
  yAxis?: number;      // the index of the y-axis (0 for primary, 1 for secondary)
  borderColor?: string;
}


export interface IHighChartsDataseries {
  name: string;
  // Allow either a simple number or a data object.
  data: Array<number | IDataPoint>;
  showInLegend?: boolean;
  type?: string;       // e.g. "column" or "spline" if needed on a per-point basis
  yAxis?: number;      // the index of the y-axis (0 for primary, 1 for secondary)
  borderColor?: string;
}
export class ReportDataObject {
  fiscalYear: number;
  granteeData: ReportGranteeDataElements[]
}
export class ReportGranteeDataElements {
  granteeName: string;
  dataElements: DataElement[]
}
export class DataElement {
  field: IField;
  value: any
}
export interface IFieldCategory {
  categoryId: number
  , categoryName: string
  , categoryDescription: string
  , categoryTooltip: string
  , categorySequenceNum: number
}

export interface IFieldSubCategory {
  categoryId: number
  , subCategoryId: number
  , subCategoryName: string
}
export interface IField {
  categoryId: number
  , categoryName: string
  , categoryDescription: string
  , categoryTooltip: string
  , categorySequenceNum: number
  , subCategoryId: number
  , subCategoryName: string
  , tableDescription: string
  , tableAlias: string
  , tableFieldName: string
  , tableFieldDescription: string
  , tableFieldId: number
  , tableFieldDataType: string
  , tableFieldAllowSum: string
  , tableFieldPrefix: string
  , tableFieldSuffix: string
  , tableFieldUnit: string
  , tableFieldAllowPieFlag: string
  , tableFieldPieGroup: string
  , tableFieldTooltip: string
  , tableFieldSelected: boolean
}
export interface IGranteeWithSelectedField {
  granteeId: number
  , granteeName: string
  , granteeAbbreviation: string
  , regionId?: number
  , granteeSelected: boolean
}
export interface IFiscalYearWithSelectedField {
  fiscalYear: number
  , final: boolean
  , fiscalYearSelected: boolean
}
export interface IReportResultColumn {
  tableAlias: string
  , tableDescription: string
  , fieldAllowSum: boolean
  , fieldDataType: string
  , fieldDescription: string
  , fieldId: number
  , fieldName: string
  , fieldPrefix: string
  , fieldSuffix: string
  , fieldIndex: number
}
export interface IReportResults {
  columnInfo: IReportResultColumn[]
  , reportData: any[]
  , includesNullValues: boolean
  , includesZeroValues: boolean
}
export interface IReportByIdResults {
  columnInfo: IReportResultColumn[]
  , reportData: any[]
  , includesNullValues: boolean
  , includesZeroValues: boolean
  , reportTitle: string
  , reportSubTitle: string
  , reportDescription: string
  , graphType: string
}

//format a value for display based on data type
export const formatChartValue = (value: any, dataType: string) => {
  if (isNaN(parseFloat(value))) {
    value = 'NA';
  }
  else {
    switch (dataType) {
      case 'CUR':
        value = '$' + Highcharts.numberFormat(value, 0, '.', ',');
        break;
      case 'CNT':
        value = Highcharts.numberFormat(value, 0, '.', ',');
        break;
      case 'PCT':
        value = Highcharts.numberFormat(value, 2, '.', ',') + '%';
        break;
      default:
        value = value;
    }
  }
  return value;
}
export const getChartType = (graphTypes) => {
  if (graphTypes.bar.graphTypeSelected) {
    return graphTypes.bar;
  }
  if (graphTypes.map.graphTypeSelected) {
    return graphTypes.map;
  }
  if (graphTypes.multiBar.graphTypeSelected) {
    return graphTypes.multiBar;
  }
  if (graphTypes.stackedBar.graphTypeSelected) {
    return graphTypes.stackedBar;
  }
  if (graphTypes.stackedBarPercent.graphTypeSelected) {
    return graphTypes.stackedBarPercent;
  }
  if (graphTypes.combo.graphTypeSelected) {
    return graphTypes.combo;
  }
  if (graphTypes.line.graphTypeSelected) {
    return graphTypes.line;
  }
  if (graphTypes.pie.graphTypeSelected) {
    return graphTypes.pie;
  }
  if (graphTypes.table.graphTypeSelected) {
    return graphTypes.table;
  }
}
export const ConvertToNumber = (stringValue: string) => {
  return Number(stringValue.replace(/,/g, "").replace("$", "").replace("%", ""));
}
export const createReportDataObject = (reportData: any[], selectedFields: any[]) => {
  let dataObjectArray = [];
  let dataObject: ReportDataObject = new ReportDataObject();
  let granteeDataElement: ReportGranteeDataElements = new ReportGranteeDataElements();
  let dataElement: DataElement = new DataElement();
  let prevFiscalYear = 0;
  let prevGrantee = "";
  const FISCAL_YEAR = "Fiscal Year";
  const GRANTEE = "Grant Recipient";
  reportData.forEach(row => {
    if (row[FISCAL_YEAR] != prevFiscalYear) {
      //add a new fiscal year
      dataObject = new ReportDataObject();
      dataObject.granteeData = [];
      dataObject.fiscalYear = row[FISCAL_YEAR];
      prevFiscalYear = row[FISCAL_YEAR];
      dataObjectArray.push(dataObject);
      prevGrantee = "";
    }
    if (row[GRANTEE] != prevGrantee) {
      //add a new grantee
      granteeDataElement = new ReportGranteeDataElements();
      granteeDataElement.dataElements = [];
      granteeDataElement.granteeName = row[GRANTEE];
      prevGrantee = row[GRANTEE];
      dataObject.granteeData.push(granteeDataElement);
    }
    //add data elements
    selectedFields.forEach(f => {
      dataElement = new DataElement();
      dataElement.field = f;
      dataElement.value = ConvertToNumber(row[f.tableFieldDescription]);
      granteeDataElement.dataElements.push(dataElement);
    });
  });
  return dataObjectArray;
}
export const formatYAxisLabel = (dataType, pointValue) => {
  switch (dataType) {
    case 'CUR':
      return formatLabelNumber(pointValue, false, '$', '');
    case 'CNT':
      return formatLabelNumber(pointValue, false, '', '');
      break;
    case 'PCT':
      return formatLabelNumber(pointValue, false, '', '%');
      break;
    default:
      return pointValue;
  }
}

export const convertTableToCSV = (table: HTMLTableElement): string => {
  const rows = Array.from(table.rows);

  const headerRow = rows.shift() as HTMLTableRowElement;
  const headers = Array.from(headerRow.cells).map((cell) => `"${cell.innerText}"`);

  const csvRows = rows.map((row) => {
    const cells = Array.from(row.cells).map((cell) => `"${cell.innerText}"`);
    return cells.join(",");
  });

  return [headers.join(","), ...csvRows].join("\n");
}



//abbreviate number for display
export const formatLabelNumber = (numberToFormat, allowDecimals, prefix, suffix) => {
  var decimalsToShow = 0;
  if (allowDecimals) {
    decimalsToShow = 1;
  }
  var numberString = prefix;
  if (numberToFormat > 999999999)//billion                        
  {
    numberString += Highcharts.numberFormat(numberToFormat / 1000000000, 3, '.', ',') + ' B' + suffix;
  }
  else if (numberToFormat > 99999999)//hundred million                        
  {
    //no decimals since we're showing hundred millions
    numberString += Highcharts.numberFormat(numberToFormat / 1000000, 0, '.', ',') + ' M' + suffix;
  }
  else if (numberToFormat > 999999)//million                        
  {
    numberString += Highcharts.numberFormat(numberToFormat / 1000000, decimalsToShow, '.', ',') + ' M' + suffix;
  }
  else if (numberToFormat > 9999)//ten thousand                        
  {
    numberString += Highcharts.numberFormat(numberToFormat / 1000, decimalsToShow, '.', ',') + ' K' + suffix;
  }
  else {
    numberString += Highcharts.numberFormat(numberToFormat, 0, '.', ',') + suffix;
  }
  return numberString;
}

export class ReportWorker extends baseWorker {
  public async getFieldSubCategories(): Promise<IField[]> {
    
    const response: AxiosResponse =
      //using the apiBaseURL property defined in config.ts which we import above
      await axiosInstance.get(`${config.apiBaseURL}/search/custom/fieldcategories`);
    let results: IField[] = [];
    response.data.forEach(r => {
      results.push({
        categoryId: r.CATG_ID
        , categoryName: r.CATG_NAME
        , categoryDescription: r.CATG_DESC
        , categoryTooltip: r.CATG_TOOLTIP
        , categorySequenceNum: r.CATG_SEQ_NUM
        , subCategoryId: r.SCAT_ID
        , subCategoryName: r.SCAT_NAME
        , tableDescription: r.TABL_DESC
        , tableAlias: r.TABL_ALIAS
        , tableFieldName: r.TFLD_NAME
        , tableFieldDescription: r.TFLD_DESC
        , tableFieldId: r.TFLD_ID
        , tableFieldDataType: r.TFLD_DATA_TYPE
        , tableFieldAllowSum: r.TFLD_ALLOW_SUM
        , tableFieldPrefix: r.TFLD_PREFIX
        , tableFieldSuffix: r.TFLD_SUFFIX
        , tableFieldUnit: r.TFLD_UNIT
        , tableFieldAllowPieFlag: r.TFLD_ALLOW_PIE_FLAG
        , tableFieldPieGroup: r.TFLD_PIE_GROUP
        , tableFieldTooltip: r.TFLD_TOOLTIP
        , tableFieldSelected: false
      });
    });
    return results;
  }
  public async getReportDataById(reportId: number
    , grantees: string
    , years: string): Promise<IReportByIdResults> {

    //add parameters to formdata
    const formData = new FormData();
    formData.append('grantees', grantees);
    formData.append('years', years);

    
    const response: AxiosResponse =
      //using the apiBaseURL property defined in config.ts which we import above
      await axiosInstance.post(`${config.apiBaseURL}/search/custom/getreport/${reportId}`, formData);
    //get the columns and build a results array with 
    //fiscal year, grantee, then columns from cols collection
    const GET_BYID_DATAROWS_INDEX = 0;
    const GET_BYID_REPORTMETADATA_INDEX = 1;
    const GET_BYID_FIELDINFO_INDEX = 2;
    let results: IReportResults = this.buildResults(response.data[GET_BYID_FIELDINFO_INDEX]
      , response.data[GET_BYID_DATAROWS_INDEX]);
    let reportMetaData: any[] = response.data[GET_BYID_REPORTMETADATA_INDEX][0];


    let resultsById: IReportByIdResults = {
      columnInfo: results.columnInfo
      , reportData: results.reportData
      , includesNullValues: results.includesNullValues
      , includesZeroValues: results.includesZeroValues
      , reportDescription: reportMetaData['GSRP_DESC']
      , reportTitle: reportMetaData['GSRP_TITLE']
      , reportSubTitle: reportMetaData['GSRP_SUBTITLE']
      , graphType: reportMetaData['GTYP_GRAPH_TYPE_DESC']
    };

    return resultsById;
  }
  public async getReportData(fields: IField[]
    , grantees: IGranteeWithSelectedField[]
    , years: IFiscalYearWithSelectedField[]): Promise<IReportResults> {


    const fieldsString: string = fields
      .map(f => f.tableFieldId).join('|');

    let granteesString: string = grantees
      .filter(g => g.granteeSelected)
      .map(g => g.granteeId).join('|');

    if (granteesString == '') {
      granteesString = '0';
    }

    const yearsString: string = years
      .filter(y => y.fiscalYearSelected)
      .map(y => y.fiscalYear).join('|');

    const formData = new FormData();
    formData.append('fields', fieldsString);
    formData.append('grantees', granteesString);
    formData.append('years', yearsString);

    
    const response: AxiosResponse =
      //using the apiBaseURL property defined in config.ts which we import above
      await axiosInstance.post(`${config.apiBaseURL}/search/custom/getreport`, formData);

    //get the columns and build a results array with 
    //fiscal year, grantee, then columns from cols collection
    const GET_REPORT_FIELDINFO_INDEX = 0;
    const GET_REPORT_DATAROWS_INDEX = 1;

    let results: IReportResults = this.buildResults(response.data[GET_REPORT_FIELDINFO_INDEX]
      , response.data[GET_REPORT_DATAROWS_INDEX]);
    return results;
  }

  private buildResults(serviceReturnedFieldInfo: any[]
    , serviceReturnedDataRows: any[]): IReportResults {
    
    if(serviceReturnedDataRows.length == 0){
      let emptyResults: IReportResults = {
        columnInfo: []
        , reportData: []
        , includesNullValues: false
        , includesZeroValues: false
      };
      return emptyResults;  
    }


    let resultTableColumns: IReportResultColumn[] = [];
    let resultTableRows: any[] = [];
    let includesNullValues: boolean = false;
    let includesZeroValues: boolean = false;
    //get the report meta data including columns
    serviceReturnedFieldInfo.forEach(r => {
      resultTableColumns.push({
        tableAlias: r.TABL_ALIAS
        , tableDescription: r.TABL_DESC
        , fieldAllowSum: r.TFLD_ALLOW_SUM
        , fieldDataType: r.TFLD_DATA_TYPE
        , fieldDescription: r.TFLD_DESC
        , fieldId: r.TFLD_ID
        , fieldName: r.TFLD_NAME
        , fieldPrefix: r.TFLD_PREFIX
        , fieldSuffix: r.TFLD_SUFFIX
        //get the index of the column from the first data row
        , fieldIndex: Object.keys(serviceReturnedDataRows[0]).indexOf(r.TFLD_DESC)
      });
    });
    //build a results array with fiscal year, grantee, then columns from cols collection
    serviceReturnedDataRows.forEach(r => {
      let newRow = [];
      newRow["Fiscal Year"] = r["Fiscal Year"];
      newRow["Grant Recipient"] = r["Grant Recipient"];
      resultTableColumns.forEach(c => {
        let fieldValue = '';
        if (!Object.values(r)[c.fieldIndex]) {
          fieldValue = 'N/A';
          includesNullValues = true;
        }
        else if (c.fieldDataType != null) {
          //fieldValue = Number(Object.values(r)[c.fieldIndex]).toLocaleString('en-US')
          fieldValue = this.formatValue(Object.values(r)[c.fieldIndex]
            , c.fieldDataType
            , c.fieldPrefix
            , c.fieldSuffix);
          if (Number(fieldValue) == 0) {
            includesZeroValues = true;
          }
        }
        else {
          fieldValue = String(Object.values(r)[c.fieldIndex]);
        }
        //newRow[c.fieldDescription] = this.formatValue(Object.values(r)[c.fieldIndex]
        //  , c.fieldDataType
        //  , c.fieldPrefix
        //  , c.fieldSuffix);
        newRow[c.fieldDescription] = fieldValue;
      });
      resultTableRows.push(newRow);
    });
    let results: IReportResults = {
      columnInfo: resultTableColumns
      , reportData: resultTableRows
      , includesNullValues: includesNullValues
      , includesZeroValues: includesZeroValues
    };
    return results;
  }


  public formatValue(fieldValue: any
    , dataType: string
    , prefix: string
    , suffix: string): string {
    if (dataType != null && dataType != "") {
      fieldValue = Number(fieldValue).toLocaleString('en-US');
    }
    else {
      fieldValue = String(fieldValue);
    }
    return `${prefix ?? ''}${fieldValue}${suffix ?? ''}`;
  }
}
